// =============================================================================
// Session Timeout Warning Modal
// Shows countdown before automatic logout due to inactivity
// =============================================================================

import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ClockIcon } from '@heroicons/react/24/outline';

interface SessionTimeoutModalProps {
  isOpen: boolean;
  timeRemaining: number; // seconds
  onExtend: () => void;
  onLogout: () => void;
}

export function SessionTimeoutModal({
  isOpen,
  timeRemaining,
  onExtend,
  onLogout,
}: SessionTimeoutModalProps) {
  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                <div className="text-center">
                  {/* Icon */}
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
                    <ClockIcon className="h-8 w-8 text-amber-600" />
                  </div>

                  {/* Title */}
                  <Dialog.Title
                    as="h3"
                    className="mt-4 text-xl font-semibold text-gray-900"
                  >
                    Are you still there?
                  </Dialog.Title>

                  {/* Description */}
                  <p className="mt-3 text-sm text-gray-600">
                    Your session will expire due to inactivity.
                  </p>

                  {/* Countdown */}
                  <div className="mt-4 py-4 px-6 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Session expires in</p>
                    <p className="text-4xl font-bold text-gray-900 font-mono mt-1">
                      {formatTime(timeRemaining)}
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="mt-6 flex gap-3">
                    <button
                      type="button"
                      onClick={onLogout}
                      className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Log out now
                    </button>
                    <button
                      type="button"
                      onClick={onExtend}
                      className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                    >
                      I'm still here
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default SessionTimeoutModal;
